'use client'

/**
 * CustomTankInput — lets a user override the vehicle preset's tank capacity
 * with their actual usable gallons (extended-range tanks, dual saddle setups,
 * etc.). The capacity value drives the fuel-stop optimizer's lookahead range.
 *
 * UX model and validation rules mirror CustomMpgInput exactly so the two
 * "override" affordances feel like one cohesive system rather than two
 * independent forms. See custom-mpg-input.tsx for the design rationale.
 *
 * Validation:
 *   - Range 5-500 gallons covers the realistic touring spectrum (Smart Car
 *     ~9 gal up to triple-tank custom Prevost ~300 gal). 500 ceiling stops
 *     absurd inputs from breaking the optimizer's lookahead window.
 *   - Out-of-range inputs are ignored — the store only updates on valid input.
 *
 * Downstream integration:
 *   - The store's `customTankCapacity ?? vehicle.tankCapacityGallons` is what
 *     the fuel-stop optimizer reads. No engine changes — the value is passed
 *     into a pure-function planner that returns recommended stops.
 */

import { Check, Pencil, RotateCcw } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { track } from '@/lib/analytics'
import type { VehicleId } from '@/lib/types'
import { getVehicle } from '@/lib/vehicles'

interface CustomTankInputProps {
	vehicleId: VehicleId
	value: number | null
	onChange: (gallons: number | null) => void
}

export function CustomTankInput({ vehicleId, value, onChange }: CustomTankInputProps) {
	const vehicle = getVehicle(vehicleId)
	const [isEditing, setIsEditing] = useState(value !== null)
	const [inputValue, setInputValue] = useState(value === null ? '' : String(value))

	// If vehicle changes (which restores the new vehicle's saved override or null
	// in the store), collapse the editor and reset the input string accordingly.
	useEffect(() => {
		if (value === null) {
			setIsEditing(false)
			setInputValue('')
		} else {
			setInputValue(String(value))
		}
	}, [value])

	const handleChange = (raw: string) => {
		setInputValue(raw)
		if (raw === '') {
			onChange(null)
			return
		}
		const parsed = Number.parseFloat(raw)
		if (Number.isFinite(parsed) && parsed >= 5 && parsed <= 500) {
			// Track only the first valid override of a session — onChange fires
			// per keystroke, so we'd otherwise spam the analytics endpoint.
			if (value === null) {
				track('OverrodeTankCapacity', { vehicle: vehicleId, gallons: parsed })
			}
			onChange(parsed)
		}
	}

	const handleReset = () => {
		setIsEditing(false)
		setInputValue('')
		onChange(null)
	}

	const handleDone = () => {
		if (value !== null) setIsEditing(false)
	}

	const hasCustom = value !== null

	if (!isEditing) {
		return (
			<div className="flex items-center justify-between gap-2">
				<div className="text-sm">
					<span className="text-text-secondary">Tank: </span>
					<span className="font-mono font-semibold text-text-primary">
						{hasCustom ? value : vehicle.tankCapacityGallons}
					</span>
					<span className="ml-0.5 text-text-muted"> gal</span>
					<span
						className={`ml-1.5 text-[10px] uppercase tracking-wider ${hasCustom ? 'text-brand' : 'text-text-muted'}`}
					>
						{hasCustom ? 'custom' : 'preset'}
					</span>
				</div>
				<div className="flex items-center gap-1.5 shrink-0">
					<button
						type="button"
						onClick={() => setIsEditing(true)}
						className="inline-flex items-center gap-1 rounded-md border border-border bg-surface px-2 py-1 text-xs font-medium text-text-secondary transition-colors hover:border-brand/60 hover:text-text-primary"
					>
						<Pencil className="size-3" aria-hidden />
						{hasCustom ? 'Edit' : 'Override'}
					</button>
					{hasCustom && (
						<button
							type="button"
							onClick={handleReset}
							className="inline-flex items-center gap-1 rounded-md border border-border bg-surface px-2 py-1 text-xs font-medium text-text-muted transition-colors hover:border-error-fg/60 hover:text-error-fg"
							title={`Reset to preset (${vehicle.tankCapacityGallons} gal)`}
						>
							<RotateCcw className="size-3" aria-hidden />
							Reset
						</button>
					)}
				</div>
			</div>
		)
	}

	return (
		<fieldset className="rounded-md border border-brand/30 bg-brand/5 px-3 py-2.5">
			<legend className="flex w-full items-center justify-between text-sm">
				<span className="font-semibold text-text-primary">Custom Tank Capacity</span>
				<button
					type="button"
					onClick={handleReset}
					className="flex items-center gap-1 text-xs text-text-muted hover:text-text-primary transition-colors"
				>
					<RotateCcw className="size-3" aria-hidden />
					Use preset ({vehicle.tankCapacityGallons} gal)
				</button>
			</legend>
			<div className="mt-2 flex items-center gap-2">
				<Input
					type="number"
					inputMode="decimal"
					min={5}
					max={500}
					step={1}
					value={inputValue}
					onChange={(e) => handleChange(e.target.value)}
					placeholder={String(vehicle.tankCapacityGallons)}
					className="font-mono flex-1"
					aria-label="Custom tank capacity in gallons"
				/>
				<Button
					type="button"
					variant="default"
					size="sm"
					onClick={handleDone}
					disabled={!hasCustom}
					title={
						hasCustom
							? 'Close editor (value is already saved)'
							: 'Enter a valid capacity (5–500 gal)'
					}
				>
					<Check className="size-3.5" aria-hidden />
					Done
				</Button>
			</div>
			<p className="mt-1.5 text-[10px] text-text-muted">
				Used to plan fuel stops along your route. Saved automatically as you type.
			</p>
		</fieldset>
	)
}
